import { SuggestedTimeOption } from "#webapp/src/components/chat-plus-calendar/PersistedProposalContext";
import { Button, Skeleton } from "@clockwise/design-system";
import { ClockwiseIntelligenceFilled } from "@clockwise/design-system/icons";
import { EventAvailable } from "@clockwise/icons";
import { ProposalState, TradeoffType } from "@clockwise/schema/v2";
import { AttendeeAvatar } from "@clockwise/web-commons/src/ui/AttendeeAvatar";
import { AttendeeAvatarStack } from "@clockwise/web-commons/src/ui/AttendeeAvatarStack";
import { Profile } from "@clockwise/web-commons/src/util/profile.util";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import classNames from "classnames";
import { Interval } from "luxon";
import pluralize from "pluralize";
import { default as React } from "react";
import { TradeoffBlock } from "../../../chat-plus-calendar/PersistedProposalContext";
import { formattedDateTime } from "../../../web-app-calendar/calendar-popover/utils/formattedDateTime";
import { ECTitle } from "../ECTitle";

const TIME_OPTIONS_SHARED_CLASSES =
  "cw-border-solid cw-font-medium cw-group cw-border cw-border-subtle cw-p-2 cw-mb-2 cw-rounded-lg cw-flex cw-justify-between hover:cw-bg-busy-inset hover:cw-bg-opacity-50";

const RELEVANT_TRADEOFF_TYPES = new Set([
  TradeoffType.AvailabilityIssue,
  TradeoffType.FixableConflict,
]);

const mapProposalOptionCategoryToColor = (tradeoffType?: TradeoffType): string => {
  switch (tradeoffType) {
    case TradeoffType.FixableConflict:
      return "cw-text-busy";
    case TradeoffType.AvailabilityIssue:
      return "cw-text-warning";
  }

  return "cw-text-subtle";
};

const shouldShowAffectedAttendees = (tradeoffType?: TradeoffType): boolean => {
  if (!tradeoffType) {
    return false;
  }

  return RELEVANT_TRADEOFF_TYPES.has(tradeoffType);
};

const getAffectedAttendees = (tradeoffBlocks: TradeoffBlock[]): Profile[] => {
  const relevantBlocks = tradeoffBlocks.filter((block) =>
    RELEVANT_TRADEOFF_TYPES.has(block.tradeoffType),
  );

  return relevantBlocks
    .flatMap((block) => block.affectedAttendees)
    .map((affectedAttendee) => ({
      givenName: affectedAttendee.person.displayName,
      externalImageUrl: affectedAttendee.person.externalImageUrl,
    }));
};

const getOverridingTradeoffType = (tradeoffBlocks: TradeoffBlock[]): TradeoffType | undefined => {
  if (tradeoffBlocks.length === 0) {
    return undefined;
  }
  if (
    tradeoffBlocks.filter((block) => block.tradeoffType === TradeoffType.AvailabilityIssue).length >
    0
  ) {
    return TradeoffType.AvailabilityIssue;
  }
  if (
    tradeoffBlocks.filter((block) => block.tradeoffType === TradeoffType.FixableConflict).length > 0
  ) {
    return TradeoffType.FixableConflict;
  }

  return undefined;
};

const sortIntervalsAscending = (a: Interval, b: Interval): number => {
  const [aStart, bStart] = [a.start, b.start];
  if (aStart === bStart) {
    return 0;
  }
  if (aStart < bStart) {
    return -1;
  }
  return 1;
};

const getTimeSuggestionsCopy = (tradeoffBlocks: TradeoffBlock[]): string => {
  const tradeoffType = getOverridingTradeoffType(tradeoffBlocks);
  let result = "";

  if (!tradeoffType) {
    return "No conflicts";
  }

  const affectedAttendees = getAffectedAttendees(tradeoffBlocks);
  if (tradeoffType === TradeoffType.AvailabilityIssue) {
    result = pluralize("Conflict", affectedAttendees.length);
  } else if (tradeoffType === TradeoffType.FixableConflict) {
    result = pluralize("Fixable Conflict", affectedAttendees.length);
  }

  return result;
};

const TimeSuggestionsContent = ({
  interval,
  tradeoffBlocks,
}: {
  interval: Interval;
  tradeoffBlocks: TradeoffBlock[];
}) => {
  const tradeoffType = getOverridingTradeoffType(tradeoffBlocks);

  return (
    <div className={classNames("cw-flex cw-flex-col")}>
      <div className="cw-body-sm  cw-text-default cw-font-medium">
        {formattedDateTime(interval.start, getRenderTimeZone())}
      </div>
      <div className={classNames("cw-flex cw-over cw-body-sm cw-gap-1 cw-items-center")}>
        {shouldShowAffectedAttendees(tradeoffType) ? (
          <AttendeeAvatarStack
            maxShown={3}
            size="medium"
            disableOverflow={true}
            disableAnimation={true}
          >
            {getAffectedAttendees(tradeoffBlocks).map((attendee) => (
              <AttendeeAvatar
                key={attendee.familyName}
                profile={attendee}
                size="medium"
                borderColor={"white"}
              />
            ))}
          </AttendeeAvatarStack>
        ) : (
          <EventAvailable fontSize="inherit" className={classNames("cw-fill-muted")} />
        )}
        <div
          className={classNames(
            mapProposalOptionCategoryToColor(tradeoffType),
            "cw-text-11 cw-pt-s2 cw-font-medium",
          )}
        >
          {getTimeSuggestionsCopy(tradeoffBlocks)}
        </div>
      </div>
    </div>
  );
};

const TimeSuggestionsChooseButton = ({
  shouldBeVisible,
  onChooseTime,
  classOverrides = undefined,
}: {
  shouldBeVisible: boolean;
  onChooseTime: () => void;
  classOverrides?: string;
}) => {
  return (
    <div
      className={classNames(
        "cw-invisible",
        {
          "group-hover:cw-visible": shouldBeVisible,
        },
        classOverrides,
      )}
    >
      <Button
        aria-label="Save"
        onClick={onChooseTime}
        size="xsmall"
        variant="outlined"
        sentiment="neutral"
      >
        Choose
      </Button>
    </div>
  );
};

const HighlightedSuggestion = ({
  timeOption,
  onUpdateTime,
  onHover,
  selectedInterval,
  title = undefined,
  classes = undefined,
  onChooseTime,
}: {
  timeOption: SuggestedTimeOption;
  onUpdateTime: (interval: Interval) => void;
  onHover: (interval: Interval | null) => void;
  onChooseTime: () => void;
  selectedInterval: Interval;
  title?: string;
  classes?: string;
}) => {
  const isSelectedOption = timeOption.interval.equals(selectedInterval);

  return (
    <div
      className={classNames(
        "cw-group cw-flex cw-w-1/2 cw-flex-col cw-justify-between",
        "cw-font-medium cw-rounded-lg",
        "cw-py-2 cw-px-2.5",
        "cw-border cw-border-solid cw-border-muted",
        "hover:cw-bg-busy-inset hover:cw-bg-opacity-50",
        {
          "cw-bg-busy-inset cw-border-subtle": timeOption.interval.equals(selectedInterval),
        },
        classes,
      )}
      onMouseEnter={() => onHover(timeOption.interval)}
      onMouseLeave={() => onHover(null)}
      role="button"
      onClick={() => onUpdateTime(timeOption.interval)}
    >
      <div className={classNames("cw-pb-5")}>
        <div className={classNames("cw-flex cw-gap-1 cw-items-center")}>
          <ClockwiseIntelligenceFilled className="cw-text-busy cw-w-4 cw-h-4" />
          {title && <div className="cw-body-base cw-font-bold cw-text-13">{title}</div>}
        </div>
        <TimeSuggestionsContent
          interval={timeOption.interval}
          tradeoffBlocks={timeOption.tradeoffBlocks}
        />
      </div>
      {isSelectedOption && (
        <Button
          size="xsmall"
          variant="outlined"
          sentiment="neutral"
          aria-label="View event details for selected time"
          onClick={onChooseTime}
        >
          Choose
        </Button>
      )}
    </div>
  );
};

const LoadingSkeleton = () => {
  return (
    <div className="cw-px-3">
      <div className={classNames("cw-flex cw-gap-2")}>
        {/* Highlighted time options skeletons */}
        {[1, 2].map((index) => (
          <div key={index} className={classNames("cw-w-1/2 cw-flex-col cw-mb-2 cw-h-[128px]")}>
            <Skeleton className="cw-h-full" variant="rounded" />
          </div>
        ))}
      </div>

      {/* Regular time options skeletons */}
      {[1, 2, 3].map((index) => (
        <div key={index} className="cw-mb-2 cw-h-[56px]">
          <Skeleton className="cw-h-full" variant="rounded" />
        </div>
      ))}
    </div>
  );
};

export const TimeSuggestions = ({
  title,
  suggestedTimeOptions,
  selectedInterval,
  proposalStatus,
  readOnly,
  onChooseTime,
  onUpdateTime,
  onUpdateTitle,
  onHoverTimeSuggestion,
}: {
  title: string;
  proposalStatus: ProposalState;
  selectedInterval: Interval;
  suggestedTimeOptions: SuggestedTimeOption[];
  readOnly: boolean;
  onChooseTime: () => void;
  onUpdateTime: (interval: Interval) => void;
  onHoverTimeSuggestion: (interval: Interval | null) => void;
  onUpdateTitle: (title: string) => void;
}) => {
  const loading = proposalStatus !== ProposalState.Active;

  // Give different visual treatment to the two best options returned by the scheduler
  // then chronologically sort the rest
  const highlightedSuggestions =
    suggestedTimeOptions.length >= 2 ? suggestedTimeOptions.slice(0, 2) : suggestedTimeOptions;
  const sortedTimeOptions =
    suggestedTimeOptions.length >= 2
      ? suggestedTimeOptions.slice(2).sort((a, b) => sortIntervalsAscending(a.interval, b.interval))
      : [];

  return (
    <div className="cw-space-y-2 cw-pb-2">
      <ECTitle
        eventName={title}
        readOnly={readOnly}
        onEventNameChange={(e) => {
          onUpdateTitle(e.target.value);
        }}
      />
      <div
        className={classNames(
          "cw-flex cw-items-center cw-justify-between",
          "cw-py-2 cw-px-3 cw-mb-2",
          "cw-body-sm cw-font-semibold cw-text-subtle",
        )}
      >
        Results
      </div>
      {loading ? (
        <LoadingSkeleton />
      ) : (
        <>
          <div className="cw-flex cw-gap-2 cw-mx-3">
            {highlightedSuggestions.map((timeOption) => (
              <HighlightedSuggestion
                key={timeOption.interval.start.toString()}
                timeOption={timeOption}
                onUpdateTime={onUpdateTime}
                onHover={onHoverTimeSuggestion}
                onChooseTime={onChooseTime}
                title="Recommended"
                selectedInterval={selectedInterval}
              />
            ))}
          </div>
          {sortedTimeOptions.map(({ interval, tradeoffBlocks }) => (
            <div
              key={interval.start.toString()}
              className={classNames(
                TIME_OPTIONS_SHARED_CLASSES,
                {
                  "cw-bg-busy-inset cw-border-muted": interval.equals(selectedInterval),
                },
                "cw-mx-3",
              )}
              role="button"
              onClick={() => onUpdateTime(interval)}
              onMouseEnter={() => onHoverTimeSuggestion(interval)}
              onMouseLeave={() => onHoverTimeSuggestion(null)}
            >
              <TimeSuggestionsContent interval={interval} tradeoffBlocks={tradeoffBlocks} />
              <TimeSuggestionsChooseButton
                shouldBeVisible={interval.start.equals(selectedInterval.start)}
                onChooseTime={onChooseTime}
                classOverrides={"cw-pt-1"}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};
